import React, { useState, useCallback } from "react";
import {
  ChoiceList,
  TextField,
  Icon,
  Tag,
  InlineStack,
  BlockStack,
} from "@medentic/polaris-react";
import { SearchIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";

export interface MultiComboOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  /** All available options for the user to pick from */
  options: MultiComboOption[];

  /** Currently selected values (array of strings) */
  selected: string[];

  /** Called when the user toggles an option or removes a tag */
  onChange: (newSelected: string[]) => void;

  /** Localized label for the TextField (defaults to 'Search tags') */
  label?: string;

  /** Localized placeholder text for the TextField (defaults to 'Search tags') */
  placeholder?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  label,
  placeholder,
}) => {
  /**
   * 1) Use the i18n hook
   */
  const { t } = useTranslation();

  /**
   * 2) Provide default localizable text if none is passed via props
   */
  const computedLabel = label ?? t("MultiSelectCombobox.label");
  const computedPlaceholder = placeholder ?? t("MultiSelectCombobox.placeholder");

  /**
   * 3) The rest of your existing multi-select logic
   */
  const [inputValue, setInputValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);

  const escapeRegex = useCallback(
    (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    []
  );

  const updateText = useCallback(
    (value: string) => {
      setInputValue(value);

      if (!value) {
        setFilteredOptions(options);
        return;
      }

      const filterRegex = new RegExp(escapeRegex(value), "i");
      const resultOptions = options.filter((option) =>
        option.label.match(filterRegex)
      );
      setFilteredOptions(resultOptions);
    },
    [options, escapeRegex]
  );

  const handleChoiceListChange = useCallback(
    (selectedValues: string[]) => {
      onChange(selectedValues);
    },
    [onChange]
  );

  const choiceListOptions = filteredOptions.slice(0, 10).map((option) => ({
    label: option.label,
    value: option.value,
  }));

  return (
    <BlockStack gap="200">
      <TextField
        prefix={<Icon source={SearchIcon} />}
        onChange={updateText}
        label={computedLabel}
        labelHidden
        value={inputValue}
        placeholder={computedPlaceholder}
        autoComplete="off"
      />

      <ChoiceList
        title={""}
        allowMultiple
        choices={choiceListOptions}
        selected={selected}
        onChange={handleChoiceListChange}
      />

      <InlineStack gap="200">
        {selected.map((value) => {
          const foundOption = options.find((opt) => opt.value === value);
          const tagLabel = foundOption?.label ?? value;

          return (
            <Tag
              key={value}
              onRemove={() => onChange(selected.filter((v) => v !== value))}
            >
              {tagLabel}
            </Tag>
          );
        })}
      </InlineStack>
    </BlockStack>
  );
};
